/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ServiceManager } from '@jutro/legacy/services';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { platformMessages } from 'ml-components-platform-react';
import _ from 'lodash';

import { MLGeoCompleteService } from 'ml-services-common-react';
import { AddressLookupService } from 'gw-capability-address';
import appConfig from 'app-config';

import policyChangeMetadata from './MLAddressDetailsComponent.policychange.metadata.json5';
import lossLocationMetadata from './MLAddressDetailsComponent.losslocation.metadata.json5';
import claimPortalMetadata from './MLAddressDetailsComponent.claimportal.metadata.json5';
import styles from './MLAddressDetailsComponent.module.scss';

// ML: AddressDetails.js

function MLAddressDetailsComponent(props) {
    const {
        id,
        onValidate,
        showValidationErrors,
        //
        addressVM,
        onModelChange,
        onModelBlur,
        readOnly,
        layout,
        isStateLock,
        isCityLock,
        cityStateLookUp,
        addressLine2PlaceHolder
    } = props;
    const isQnB = appConfig.portalCode === 'QB';
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const { onValidate: setComponentValidation, registerComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    /* 
     * If the component is no longer visible in the modal,
     * set it's validation to true to avoid FormModalPopover's isComponentValid from getting stuck with an invalid validation that no longer matters
    */
    useEffect(() => {
        return () => {
            if(onValidate){
                onValidate(true, id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(()=> {
        const localeService = ServiceManager.getService('locale-service');
        const defaultCountryCode = localeService.getDefaultCountryCode();
        if(!addressVM.country.value) {
            _.set(addressVM, 'country.value', _.get(addressVM, 'country.value', defaultCountryCode));
        }

    }, [addressVM]);

    const [lockState, setLockState] = useState(isStateLock);
    const [lockCity, setLockCity] = useState(isCityLock);

    const constructCityStateCode = () => {
        const city = _.defaultTo(_.get(addressVM, 'city.value'), '');
        const stateCode = _.defaultTo(_.get(addressVM, 'state.value.code'), '');
        return `${city}, ${stateCode}`;
    };

    const isValidPostalCode = (postalCode) => {
        return /^\d{5}(-\d{4})?$/.test(postalCode);
    };

    const autoFillCityAndState = (postalCode) => {
        const lookUpParams = {
            postalCode: postalCode.substring(0, 5)
        };
        const lookUpPromise = AddressLookupService.lookUpCityAndState(lookUpParams, authHeader);
        lookUpPromise.then(
            (lookUpResp) => {
                addressVM.city.value = lookUpResp.city ? lookUpResp.city : undefined;
                setLockCity(addressVM.city.value !== undefined);
                addressVM.state.value = lookUpResp.state ? lookUpResp.state : undefined;
                setLockState(addressVM.state.value !== undefined);
            },
            () => {
                setLockCity(false);
                setLockState(false);
            }
        );
    };

    const lookupStatesCallback = (postalCode, states) => {
        if (states && states.length > 1) {
            setLockCity(false);
            setLockState(false);
            // TODO
        } else {
            const isDataPrepopulated = states && states.length === 1 && !!states[0].City && !!states[0].StateProvince;
            if (isDataPrepopulated) {
                addressVM.state.value = states[0].StateProvince;
                addressVM.city.value = states[0].City;
                setLockCity(addressVM.city.value !== undefined);
                setLockState(addressVM.state.value !== undefined);
            } else {
                autoFillCityAndState(postalCode);
            }
        }
    };

    const lookupAddress = async (postalCode) => {
        try {
            const states = await MLGeoCompleteService.getStates(postalCode.substring(0, 5));
            lookupStatesCallback(postalCode, states);
        } catch (err) {
            lookupStatesCallback(postalCode);
        }
        onModelChange(addressVM);
    };

    const onValueChange = (value, path) => {
        if (cityStateLookUp && path === 'postalCode' && isValidPostalCode(value)) {
            lookupAddress(value);
        } else {
            onModelChange(addressVM);
        }
    };

    const displayClaimState = () => _.get(addressVM, 'country.value') ? addressVM.country.value.code === 'CA' || addressVM.country.value.code === 'US' : false;

    const resolvers = {
        resolveComponentMap: {
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
        }
    };

    const policyChangOverrides = {
        addressLine1Id: {
            disabled: readOnly,
            onBlur: onModelBlur,
            className: readOnly? "mlReadOnlyFieldComponent vertical-spacing-10" : "vertical-spacing-10"
        },
        addressLine2Id: {
            disabled: readOnly,
            onBlur: onModelBlur,
            placeholder: addressLine2PlaceHolder ? translator(addressLine2PlaceHolder) : translator(platformMessages.mlAptUnit),
            className: readOnly? "mlReadOnlyFieldComponent vertical-spacing-10" : "vertical-spacing-10"
        },
        lockCityStateId: {
            visible: lockCity && lockState,
            content: constructCityStateCode()
        },
        noLockCityStateId: {
            visible: !(lockCity && lockState)
        },
        postcodeIdContainer: {
            className: readOnly? "col-lg-4 col-md-6 col-sm-12" : "col-lg-4 col-md-6 col-sm-12"
        },
        postcodeId: {
            disabled: readOnly,
            onBlur: onModelBlur,
            className: readOnly ? "mlReadOnlyFieldComponent" : "",
            placeholder: isQnB ? "Zipcode" : "#####"
        },
        cityId: {
            disabled: lockCity,
            onBlur: onModelBlur
        },
        stateId: {
            readOnly: lockState,
            onBlur: onModelBlur
        },
        flexwrapperRow: {
            className: isQnB ? "cityStateQB" : (lockCity && lockState ? 'row flex-wrapper align-items-center vertical-spacing-10' : 'row flex-wrapper vertical-spacing-10')
        }
    };

    const lossLocationOverrides = {
        country: {
            disabled: readOnly
        },
        state: {
            visible: displayClaimState(),
            disabled: readOnly
        },
        city: {
            disabled: readOnly
        }
    }

    const claimPortalOverrides = {
        stateCol: {
            visible: addressVM.country && displayClaimState()
        }
    }

    const resolveUiProps = () => {
        switch (layout) {
            case 'ml-policy-change':
                return { content: policyChangeMetadata.componentContent }; 
            case 'ml-lossLocation':
                return { content: lossLocationMetadata.componentContent };
            case 'ml-cp':
                return { content: claimPortalMetadata.componentContent };
            default:
                return { content: '' };
        }
    };

    const resolveOverrides = () => {
        switch (layout) {
            case 'ml-policy-change':
                return policyChangOverrides; 
            case 'ml-lossLocation':
                return lossLocationOverrides;
            case 'ml-cp':
                return claimPortalOverrides;
            default:
                return { content: '' };
        }
    };

    return (
        <ViewModelForm
            uiProps={resolveUiProps().content}
            overrideProps={resolveOverrides()}
            model={addressVM}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
            onValueChange={onValueChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLAddressDetailsComponent.propTypes = {
    id: PropTypes.string.isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    //
    addressVM: PropTypes.shape({
        aspects: PropTypes.shape({
            valid: PropTypes.bool,
            subtreeValid: PropTypes.bool
        }),
        city: PropTypes.shape({ value: PropTypes.string }),
        state: PropTypes.shape({ value: PropTypes.shape({}) })
    }).isRequired,
    onModelChange: PropTypes.func.isRequired,
    onModelBlur: PropTypes.func,
    readOnly: PropTypes.bool,
    layout: PropTypes.string,
    isStateLock: PropTypes.bool,
    isCityLock: PropTypes.bool,
    cityStateLookUp: PropTypes.bool,
    addressLine2PlaceHolder: PropTypes.shape({})
};

MLAddressDetailsComponent.defaultProps = {
    readOnly: false,
    layout: undefined,
    isStateLock: true,
    isCityLock: true,
    cityStateLookUp: true,
    addressLine2PlaceHolder: undefined,
    onModelBlur: _.noop
};

export default MLAddressDetailsComponent;
